import React, { Fragment } from 'react';

import {
  CalculatorIcon,
  ChatIcon,
  CmBaseTable,
  CmBox,
  CmCard,
  CmSimpleGrid,
  CmTableBodyRow,
  CmTd,
  CmText,
  DeliveryIcon,
  DishIcon,
  LinkIcon,
  ReceiptIcon,
  StoreIcon,
} from '@/components/uiParts';
import {
  DeliveryServiceBadge,
  OrderStatusBadge,
} from '@/pages/externals/mos-pos-resend/components/Badges';
import { DetailField } from '@/pages/externals/mos-pos-resend/detail/components/DetailField';
import {
  CHILD_ITEM_COLOR,
  DEMAECAN_DELIVERY_SERVICE_ID,
  DISCOUNT_AMOUNT_COLOR,
  displayOrEmpty,
  EMPTY_VALUE,
  formatDateTime,
  formatDateTimeWithSeconds,
  formatDay,
  formatDiscountPrice,
  formatPrice,
  formatTime,
  ITEM_FOOTER_BORDER_COLOR,
  ITEM_FOOTER_SUBTOTAL_BG,
  ITEM_FOOTER_TOTAL_BG,
  ITEM_FOOTER_TOTAL_COLOR,
  itemTableThs,
  NOTES_BG,
  PAYMENT_AMOUNT_COLOR,
  PICKUP_CONDITION_LABEL,
  SECTION_HEAD_BG,
  SECTION_HEAD_BORDER_COLOR,
  SECTION_HEAD_GAP,
  SECTION_HEAD_ICON_COLOR,
  SECTION_HEAD_ICON_SIZE,
  shouldShowItemPrice,
  shouldShowItemUnitPrice,
  UBER_EATS_DELIVERY_SERVICE_ID,
} from '@/pages/externals/mos-pos-resend/service';
import {
  MosPosOrderDetailResponse,
  MosPosOrderItem,
} from '@/services/MosPosOrder/type';

type SectionProps = {
  title: string;
  /** テンプレートの .section-head 先頭に並ぶアイコン */
  icon: React.ElementType;
};

/** テンプレートの .section: 見出しはアイコン付きの背景色付きの帯、本文はその下 */
const Section: React.FC<SectionProps> = ({ title, icon: Icon, children }) => (
  <CmCard mb="16px" padding="0" overflow="hidden">
    <CmText
      display="flex"
      alignItems="center"
      gridGap={SECTION_HEAD_GAP}
      bg={SECTION_HEAD_BG}
      borderBottomWidth="1px"
      borderBottomColor={SECTION_HEAD_BORDER_COLOR}
      padding="12px 18px"
      fontSize="13.5px"
      fontWeight="bold"
    >
      <Icon boxSize={SECTION_HEAD_ICON_SIZE} color={SECTION_HEAD_ICON_COLOR} />
      {title}
    </CmText>
    <CmBox padding="6px 18px 14px">{children}</CmBox>
  </CmCard>
);

/** テンプレートの .grid2: 項目は 2 カラムに流し込む */
const FieldGrid: React.FC = ({ children }) => (
  <CmSimpleGrid columns={2} columnGap="40px">
    {children}
  </CmSimpleGrid>
);

type ItemRowProps = {
  item: MosPosOrderItem;
  isChild: boolean;
};

/**
 * 明細1行。node_type によって金額の意味が違うため分岐が必要。
 * - set_parent: price は調整額でマイナスもありうる。単価の概念がないので「-」
 * - accessory : マスタ価格が常に ¥0。¥0 と表示すると不具合に見えるため金額を出さない
 */
const ItemRow: React.VFC<ItemRowProps> = ({ item, isChild }) => {
  const showPrice = shouldShowItemPrice(item.node_type);
  const showUnitPrice = shouldShowItemUnitPrice(
    item.node_type,
    item.unit_price
  );

  return (
    <CmTableBodyRow>
      <CmTd>
        <CmText fontFamily="mono">{isChild ? '' : item.menu_code}</CmText>
      </CmTd>
      <CmTd
        {...(isChild
          ? { color: CHILD_ITEM_COLOR, fontSize: '12px', pl: '22px' }
          : {})}
      >
        {/* 「（プロモ適用）」は画面設計書 No.50「1. 親商品の表示規則」の項目。
            子行 (同 No.50「2. オプション項目の表示規則」) には付けない。 */}
        {isChild
          ? `└ オプション: ${item.menu_name}`
          : `${item.menu_name}${
              item.promotion_applied ? '（プロモ適用）' : ''
            }`}
      </CmTd>
      <CmTd textAlign="right">{item.quantity}</CmTd>
      <CmTd textAlign="right">
        {showUnitPrice && item.unit_price !== null
          ? formatPrice(item.unit_price)
          : EMPTY_VALUE}
      </CmTd>
      <CmTd textAlign="right">
        {showPrice ? formatPrice(item.price) : EMPTY_VALUE}
      </CmTd>
    </CmTableBodyRow>
  );
};

type Props = {
  detail: MosPosOrderDetailResponse;
};

/** SCR002 / SCR003 で共通の表示項目 */
export const OrderDetailSections: React.VFC<Props> = ({ detail }) => {
  const { amount, platform, store, pickup } = detail;
  const isUberEats =
    detail.delivery_service_id === UBER_EATS_DELIVERY_SERVICE_ID;
  const isDemaecan =
    detail.delivery_service_id === DEMAECAN_DELIVERY_SERVICE_ID;

  return (
    <>
      <Section title="注文基本情報" icon={ReceiptIcon}>
        <FieldGrid>
          <DetailField
            label="WebオーダーID"
            value={
              <CmText fontFamily="mono" fontWeight="bold">
                {detail.web_order_id}
              </CmText>
            }
          />
          {/* テンプレートの .field.no-line と同じく 2 カラム目を空ける */}
          <CmBox />
          <DetailField
            label="注文ステータス"
            value={<OrderStatusBadge status={detail.status} />}
          />
          <DetailField
            label="注文ステータス更新日時"
            value={formatDateTimeWithSeconds(detail.status_updated_at)}
          />
        </FieldGrid>
      </Section>

      <Section title="プラットフォーム情報" icon={LinkIcon}>
        <FieldGrid>
          <DetailField
            label="デリバリーサービス"
            value={
              <DeliveryServiceBadge
                deliveryServiceId={detail.delivery_service_id}
              />
            }
          />
          <DetailField
            label="Uber Eats注文ID"
            value={
              isUberEats ? displayOrEmpty(platform.uber_order_id) : EMPTY_VALUE
            }
          />
          <DetailField
            label="注文識別用固有番号"
            value={
              isUberEats
                ? displayOrEmpty(platform.uber_display_id)
                : EMPTY_VALUE
            }
          />
          <DetailField
            label="出前館注文ID"
            value={
              isDemaecan
                ? displayOrEmpty(platform.demaecan_order_id)
                : EMPTY_VALUE
            }
          />
          <DetailField
            label="出前館受取用番号"
            value={
              isDemaecan
                ? displayOrEmpty(platform.demaecan_pickup_id)
                : EMPTY_VALUE
            }
          />
        </FieldGrid>
      </Section>

      <Section title="店舗情報" icon={StoreIcon}>
        <FieldGrid>
          <DetailField
            label="店舗コード"
            value={<CmText fontFamily="mono">{store.store_code}</CmText>}
          />
          {/* 店舗名末尾の「店」は省略しない */}
          <DetailField
            label="店舗名"
            value={displayOrEmpty(store.store_name)}
          />
        </FieldGrid>
      </Section>

      <Section title="受取情報" icon={DeliveryIcon}>
        <FieldGrid>
          {/* 受取条件は API が返さないため固定値 */}
          <DetailField label="受取条件" value={PICKUP_CONDITION_LABEL} />
          <CmBox />
          <DetailField
            label="注文日"
            value={formatDay(pickup.order_date_and_time)}
          />
          <DetailField
            label="注文時刻"
            value={formatTime(pickup.order_date_and_time)}
          />
          <DetailField
            label="配達員到着予定時刻"
            value={formatDateTime(pickup.pickup_time)}
          />
        </FieldGrid>
      </Section>

      <Section title="金額情報" icon={CalculatorIcon}>
        <FieldGrid>
          <DetailField
            label="注文合計金額"
            value={formatPrice(amount.items_subtotal)}
          />
          <DetailField
            label="値引金額"
            value={formatDiscountPrice(amount.discount_amount)}
          />
          <DetailField
            label="支払金額"
            value={
              <CmText
                fontSize="15px"
                fontWeight="bold"
                color={PAYMENT_AMOUNT_COLOR}
              >
                {formatPrice(amount.payment_amount)}
              </CmText>
            }
          />
          <DetailField label="内消費税" value={formatPrice(amount.tax)} />
          <DetailField
            label="標準税率対象金額"
            value={formatPrice(amount.standard_rate_net_price)}
          />
          <DetailField
            label="標準税率対象消費税"
            value={formatPrice(amount.standard_rate_tax)}
          />
          <DetailField
            label="軽減税率対象金額"
            value={formatPrice(amount.reduced_rate_net_price)}
          />
          <DetailField
            label="軽減税率対象消費税"
            value={formatPrice(amount.reduced_rate_tax)}
          />
        </FieldGrid>
      </Section>

      <Section title="注文コメント" icon={ChatIcon}>
        {/* コメントがなくても枠は表示する */}
        <CmBox
          bg={NOTES_BG}
          padding="12px 14px"
          borderRadius="md"
          minHeight="48px"
          whiteSpace="pre-wrap"
          mt="8px"
        >
          {detail.order_notes}
        </CmBox>
      </Section>

      <Section title="商品リスト" icon={DishIcon}>
        <CmBox overflowX="auto">
          <CmBaseTable ths={itemTableThs}>
            {detail.items.map((item, itemIndex) => (
              <Fragment key={`${item.menu_code}-${itemIndex}`}>
                <ItemRow item={item} isChild={false} />
                {item.children.map((child, childIndex) => (
                  <ItemRow
                    key={`${itemIndex}-${child.menu_code}-${childIndex}`}
                    item={child}
                    isChild
                  />
                ))}
              </Fragment>
            ))}

            {/* No.55〜57 フッター。items[] の合算ではなく amount を使う。
                テンプレートと同じく表の行として並べ、金額列を揃える。 */}
            <CmTableBodyRow
              bg={ITEM_FOOTER_SUBTOTAL_BG}
              fontWeight="bold"
              borderTopWidth="2px"
              borderTopColor={ITEM_FOOTER_BORDER_COLOR}
            >
              <CmTd colSpan={4} textAlign="right">
                小計
              </CmTd>
              <CmTd textAlign="right">
                {formatPrice(amount.items_subtotal)}
              </CmTd>
            </CmTableBodyRow>
            <CmTableBodyRow color={DISCOUNT_AMOUNT_COLOR} fontWeight="semibold">
              <CmTd colSpan={4} textAlign="right">
                値引
              </CmTd>
              <CmTd textAlign="right">
                {formatDiscountPrice(amount.discount_amount)}
              </CmTd>
            </CmTableBodyRow>
            <CmTableBodyRow
              bg={ITEM_FOOTER_TOTAL_BG}
              color={ITEM_FOOTER_TOTAL_COLOR}
              fontSize="15px"
              fontWeight="bold"
            >
              <CmTd colSpan={4} textAlign="right">
                合計
              </CmTd>
              <CmTd textAlign="right">
                {formatPrice(amount.payment_amount)}
              </CmTd>
            </CmTableBodyRow>
          </CmBaseTable>
        </CmBox>
      </Section>
    </>
  );
};
